import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Hook to detect if the app is running inside the iOS app's WKWebView, and to
 * leave it correctly.
 *
 * Detection is based on the `source=ios` URL parameter that the iOS app adds
 * when loading the web viewer.
 *
 * @returns {{ isIOSWebView: boolean, exitToHost: (opts?: {destination?: 'project'|'projects', projectId?: string}) => void }}
 */
export const useIOSWebView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isIOSWebView = useMemo(() => {
    return searchParams.get('source') === 'ios';
  }, [searchParams]);

  /**
   * Leave this screen the way the surrounding app expects.
   *
   * In a browser that means navigating, as it always has. Inside the WebView it
   * must NOT: the WebView is the web app, so navigating renders the web project
   * page — logo, chrome and all — inside what the user believes is the iOS app,
   * with no way back to the native screens. Hand the intent to the host instead
   * and let it pop its own navigation stack.
   *
   * A host build with no `exitToHost` handler still navigates. Every phone in
   * the field is such a build, and a dead button is worse than an ugly one.
   */
  const exitToHost = useCallback(
    ({ destination = 'projects', projectId } = {}) => {
      if (isIOSWebView) {
        const handler = window.webkit?.messageHandlers?.exitToHost;
        if (handler) {
          handler.postMessage({ destination, projectId: projectId ?? null });
          return;
        }
        console.warn(
          '[exitToHost] host has no exit handler; navigating inside the WebView'
        );
      }
      navigate(
        destination === 'project' && projectId ? `/project/${projectId}` : '/'
      );
    },
    [isIOSWebView, navigate]
  );

  return { isIOSWebView, exitToHost };
};

export default useIOSWebView;
